import React from "react"
import { PlayCircle, ExternalLink } from "lucide-react"
import Thumbnail from "./Thumbnail"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

/**
 * TikTok's payload carries only a `tiktok.com` video permalink - a page, not
 * a confirmed playable stream like Instagram's reel URL (which was verified
 * against real API responses to actually serve as a <video src>, see
 * ReelPlayer.jsx). So this never attempts inline playback: it always renders
 * the mirrored `thumb_url` still frame with a "Watch on TikTok" overlay -
 * the exact same look ReelPlayer falls back to when Instagram's own inline
 * playback fails, except here it's the permanent state, not a fallback.
 *
 * Only the pill actually navigates out - clicking the rest of the tile calls
 * `onOpen` to open the in-app detail view instead, same convention as
 * ReelPlayer's fallback.
 */
const TikTokPlayer = ({ video, style, onOpen }) => {
    const { trackEvent } = useAnalytics()
    return (
    <div style={{ ...style, position: "absolute" }}>
        <Thumbnail
            src={video.thumbnail_url}
            alt={video.title}
            isInstagram={false}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div
            onClick={onOpen}
            style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: "10px",
                background: "rgba(0,0,0,0.45)", color: "#fff",
                cursor: onOpen ? "pointer" : "default",
            }}
        >
            <PlayCircle size={46} style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))" }} />
            {video.url && (
                <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        e.stopPropagation()
                        trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_OUTBOUND_CLICKED, {
                            video_id: video.video_id,
                            platform: video.platform,
                            link_type: "watch",
                        })
                    }}
                    style={{
                        display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700,
                        background: "rgba(0,0,0,0.6)", padding: "6px 12px", borderRadius: "999px",
                        color: "#fff", textDecoration: "none",
                    }}
                >
                    Watch on TikTok <ExternalLink size={13} />
                </a>
            )}
        </div>
    </div>
    )
}

export default TikTokPlayer
